/*
 * Copyright (c) 2016 Klaus Reimer
 * SPDX-License-Identifier: MIT
 */

import type { AnimationPlayer } from "./AnimationPlayer.ts";
import type { BaseImage } from "./BaseImage.ts";

/**
 * Abstract base class for animation player implementations.
 */
export abstract class BaseAnimationPlayer<A extends BaseImage, T extends BaseImage> implements AnimationPlayer {
    /** The played animation. */
    readonly #animation: A;

    /** The current frame. */
    #frame: T | null = null;

    /** The timer handle if animation is playing. Null otherwise, */
    #timer: unknown = null;

    /** The playing speed in milliseconds per time unit. Based on IBM PC BIOS timer ticks of 18.2065 Hz. */
    #speed = 1000 / 18.2065;

    /** Callback to call on each frame update. */
    readonly #onDraw: (frame: T) => void;

    /**
     * Creates a new animation player for the given animation and calling the given draw callback for each
     * animation frame.
     *
     * @param animation - The animation to play.
     * @param onDraw    - Callback to call on each frame update. This callback is responsible for actually showing the animation frame to the user.
     */
    protected constructor(animation: A, onDraw: (frame: T) => void) {
        this.#animation = animation;
        this.#onDraw = onDraw;
    }

    /**
     * Initializes the player. Called for initialization when player is created and each time it is reset.
     *
     * @param animation - The animation to play.
     * @returns The base frame to start the animation with.
     */
    protected abstract init(animation: A): T;

    /**
     * Stops and resets the animation to the base frame.
     */
    public reset(): void {
        this.stop();
        this.#onDraw(this.#frame = this.init(this.#animation));
    }

    /**
     * @returns The current animation frame.
     */
    public getFrame(): T {
        if (this.#frame == null) {
            throw new Error("Animation player did not initialize frame");
        }
        return this.#frame;
    }

    /**
     * Creates and returns the next frame in the animation.
     *
     * @param animation    - The animation to play.
     * @param currentFrame - The current frame.
     * @returns The next frame.
     */
    protected abstract nextFrame(animation: A, currentFrame: T): T;

    /**
     * Advances the animation to the next frame.
     */
    public next(): void {
        this.#onDraw(this.#frame = this.nextFrame(this.#animation, this.getFrame()));
    }

    /**
     * Returns the number of time units to wait before rendering the next frame.
     *
     * @param animation - The animation to play.
     * @returns The next delay in time units.
     */
    protected abstract getNextDelayInUnits(animation: A): number;

    /**
     * @returns the delay to wait before rendering the next frame.
     */
    public getNextDelay(): number {
        return (1 + this.getNextDelayInUnits(this.#animation)) * this.#speed;
    }

    /**
     * Starts the animation if not already running.
     */
    public start(): void {
        if (this.#timer == null) {
            const animate = (): void => {
                this.next();
                this.#timer = setTimeout(animate, this.getNextDelay());
            };
            this.#timer = setTimeout(animate, this.getNextDelay());
        }
    }

    /**
     * Stops the animation if currently running.
     */
    public stop(): void {
        if (this.#timer != null) {
            clearTimeout(this.#timer as number);
            this.#timer = null;
        }
    }

    /**
     * @returns the animation speed in milliseconds per time unit. Default is 50.
     */
    public getSpeed(): number {
        return this.#speed;
    }

    /**
     * Sets the animation speed in milliseconds per time unit. Default is 50.
     *
     * @param speed  The animation speed to set.
     */
    public setSpeed(speed: number): void {
        this.#speed = speed;
    }

    /**
     * @returns the animation width in pixels.
     */
    public getWidth(): number {
        return this.#animation.getWidth();
    }

    /**
     * @returns the animation height in pixels.
     */
    public getHeight(): number {
        return this.#animation.getHeight();
    }
}
